import {
  isOfficialPaymentOperatorResultV1,
  type OfficialPaymentOperatorResultV1,
  type OfficialPaymentRail,
} from "@billiard/contracts";

export interface OfficialPaymentCommand {
  kind: "OFFICIAL_PAYMENT";
  sessionId: string;
  tableId: string;
  tableNumber: string;
  orderId: string;
  rail: OfficialPaymentRail;
  expectedTotalFen: number;
  expectedPaymentFen?: number;
  idempotencyKey: string;
}

export type OfficialPaymentRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN" | "DIRECTED";

export interface OfficialPaymentRecovery {
  ownerIdentityKey: string;
  command: OfficialPaymentCommand;
  state: OfficialPaymentRecoveryState;
  result: OfficialPaymentOperatorResultV1 | null;
}

export type OfficialPaymentRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: OfficialPaymentRecovery }
  | { status: "OTHER_OPERATOR" }
  | { status: "RECOVERY_STORAGE_UNAVAILABLE"; pendingOutcomeMayExist: boolean };

interface StorageAccess {
  browser: boolean;
  storage: Storage | null;
}

export const OFFICIAL_PAYMENT_RECOVERY_STORAGE_KEY = "billiard-erp:official-payment-recovery:v1";

const definiteNoProviderEffectCodes = new Set([
  "AUTH_REQUIRED",
  "AUTHORIZATION_DEFAULT_DENY",
  "CSRF_REJECTED",
  "IDEMPOTENCY_COLLISION",
  "MOBILE_SESSION_ACTIVATION_REQUIRED",
  "NATIVE_APP_VERSION_INVALID",
  "NATIVE_APP_VERSION_MISMATCH",
  "NATIVE_APP_VERSION_REQUIRED",
  "NATIVE_SECURE_SESSION_UNAVAILABLE",
  "NATIVE_SERVER_UPDATE_REQUIRED",
  "NATIVE_UPDATE_REQUIRED",
  "OFFICIAL_PAYMENT_ATTEMPT_ID_CONFLICT",
  "OFFICIAL_PAYMENT_ORDER_BINDING_INVALID",
  "OFFICIAL_PAYMENT_ORDER_LOCKED",
  "OFFICIAL_PAYMENT_ORDER_UNAVAILABLE",
  "OFFICIAL_PAYMENT_PROVIDER_CAPABILITY_MISSING",
  "OFFICIAL_PAYMENT_PROVIDER_NOT_CONFIGURED",
  "OFFICIAL_PAYMENT_PROVIDER_NOT_VERIFIED",
  "OFFLINE_AUTHORITY_UNAVAILABLE",
  "ORDER_CONFLICT_ISOLATED",
  "ORIGIN_REJECTED",
  "REQUEST_VALIDATION_FAILED",
]);

let pendingRecovery: OfficialPaymentRecovery | null = null;
let dispatchingRecovery: OfficialPaymentRecovery | null = null;
let hydrated = false;
let recoveryUnavailable = false;
let pendingOutcomeMayExist = false;

export function officialPaymentIdentityKey(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): string {
  return JSON.stringify([operator.tenantId, operator.storeId, operator.id]);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(record).length === keys.length && keys.every((key) => Object.hasOwn(record, key))
  );
}

function isBoundedString(value: unknown, minimum: number, maximum: number): value is string {
  return typeof value === "string" && [...value].length >= minimum && [...value].length <= maximum;
}

function isIdentityKey(value: unknown): value is string {
  if (!isBoundedString(value, 1, 1_024)) return false;
  try {
    const identity = JSON.parse(value) as unknown;
    return (
      Array.isArray(identity) &&
      identity.length === 3 &&
      identity.every((part) => isBoundedString(part, 1, 256))
    );
  } catch {
    return false;
  }
}

function isOfficialPaymentCommand(value: unknown): value is OfficialPaymentCommand {
  const hasExpectedPayment = isRecord(value) && Object.hasOwn(value, "expectedPaymentFen");
  return (
    isRecord(value) &&
    hasExactKeys(value, [
      "kind",
      "sessionId",
      "tableId",
      "tableNumber",
      "orderId",
      "rail",
      "expectedTotalFen",
      ...(hasExpectedPayment ? ["expectedPaymentFen"] : []),
      "idempotencyKey",
    ]) &&
    value.kind === "OFFICIAL_PAYMENT" &&
    isBoundedString(value.sessionId, 1, 256) &&
    isBoundedString(value.tableId, 1, 256) &&
    isBoundedString(value.tableNumber, 1, 64) &&
    isBoundedString(value.orderId, 1, 256) &&
    value.sessionId === value.sessionId.trim() &&
    value.tableId === value.tableId.trim() &&
    value.tableNumber === value.tableNumber.trim() &&
    value.orderId === value.orderId.trim() &&
    (value.rail === "WECHAT_PAY" || value.rail === "ALIPAY") &&
    Number.isSafeInteger(value.expectedTotalFen) &&
    (value.expectedTotalFen as number) > 0 &&
    (!hasExpectedPayment ||
      (Number.isSafeInteger(value.expectedPaymentFen) &&
        (value.expectedPaymentFen as number) > 0 &&
        (value.expectedPaymentFen as number) <= (value.expectedTotalFen as number))) &&
    isBoundedString(value.idempotencyKey, 8, 128) &&
    value.idempotencyKey === value.idempotencyKey.trim()
  );
}

function resultMatchesCommand(
  result: OfficialPaymentOperatorResultV1,
  command: OfficialPaymentCommand,
): boolean {
  return (
    result.attempt.orderId === command.orderId &&
    result.attempt.rail === command.rail &&
    result.attempt.amountFen === (command.expectedPaymentFen ?? command.expectedTotalFen) &&
    result.attempt.operationTrace.idempotencyKey === command.idempotencyKey
  );
}

function immutableRecovery(
  ownerIdentityKey: string,
  command: OfficialPaymentCommand,
  state: OfficialPaymentRecoveryState,
  result: OfficialPaymentOperatorResultV1 | null,
): OfficialPaymentRecovery {
  return Object.freeze({
    ownerIdentityKey,
    command: Object.freeze(structuredClone(command)),
    state,
    result: result === null ? null : Object.freeze(structuredClone(result)),
  });
}

function storedRecovery(value: unknown): OfficialPaymentRecovery | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "recovery"]) ||
    value.version !== 1 ||
    !isRecord(value.recovery) ||
    !hasExactKeys(value.recovery, ["ownerIdentityKey", "command", "state", "result"]) ||
    !isIdentityKey(value.recovery.ownerIdentityKey) ||
    !isOfficialPaymentCommand(value.recovery.command) ||
    !["CLAIMED", "OUTCOME_UNKNOWN", "DIRECTED"].includes(String(value.recovery.state))
  ) {
    return null;
  }
  const result = value.recovery.result;
  if (
    (value.recovery.state === "DIRECTED") !== (result !== null) ||
    (result !== null &&
      (!isOfficialPaymentOperatorResultV1(result) ||
        !resultMatchesCommand(result, value.recovery.command)))
  ) {
    return null;
  }
  return immutableRecovery(
    value.recovery.ownerIdentityKey,
    value.recovery.command,
    value.recovery.state as OfficialPaymentRecoveryState,
    result as OfficialPaymentOperatorResultV1 | null,
  );
}

function recoveryEquals(left: OfficialPaymentRecovery, right: OfficialPaymentRecovery): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function sessionStorageAccess(): StorageAccess {
  if (typeof window === "undefined") return { browser: false, storage: null };
  try {
    return {
      browser: true,
      storage: typeof sessionStorage === "undefined" ? null : sessionStorage,
    };
  } catch {
    return { browser: true, storage: null };
  }
}

function readStoredRecovery(storage: Storage): OfficialPaymentRecovery | null {
  const encoded = storage.getItem(OFFICIAL_PAYMENT_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : storedRecovery(JSON.parse(encoded) as unknown);
}

function saveStoredRecovery(recovery: OfficialPaymentRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    access.storage.setItem(
      OFFICIAL_PAYMENT_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 1, recovery }),
    );
    const stored = readStoredRecovery(access.storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function verifyStoredRecovery(recovery: OfficialPaymentRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    const stored = readStoredRecovery(access.storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function hydrateStoredRecovery(): void {
  if (hydrated) return;
  hydrated = true;
  const access = sessionStorageAccess();
  if (!access.browser) return;
  if (!access.storage) {
    recoveryUnavailable = true;
    return;
  }
  try {
    const encoded = access.storage.getItem(OFFICIAL_PAYMENT_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    pendingOutcomeMayExist = true;
    const stored = storedRecovery(JSON.parse(encoded) as unknown);
    if (!stored) {
      recoveryUnavailable = true;
      return;
    }
    const recovered =
      stored.state === "CLAIMED"
        ? immutableRecovery(stored.ownerIdentityKey, stored.command, "OUTCOME_UNKNOWN", null)
        : stored;
    if (stored.state === "CLAIMED" && !saveStoredRecovery(recovered)) {
      recoveryUnavailable = true;
      return;
    }
    pendingRecovery = recovered;
  } catch {
    recoveryUnavailable = true;
  }
}

export function readOfficialPaymentRecovery(identityKey: string): OfficialPaymentRecoveryView {
  hydrateStoredRecovery();
  if (recoveryUnavailable || !isIdentityKey(identityKey)) {
    return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
  }
  if (!pendingRecovery) return { status: "NONE" };
  if (pendingRecovery.ownerIdentityKey !== identityKey) return { status: "OTHER_OPERATOR" };
  return { status: "OWNED", recovery: pendingRecovery };
}

export function claimOfficialPaymentRecovery(
  identityKey: string,
  command: OfficialPaymentCommand,
): OfficialPaymentRecoveryView {
  const current = readOfficialPaymentRecovery(identityKey);
  if (current.status !== "NONE") return current;
  if (!isIdentityKey(identityKey) || !isOfficialPaymentCommand(command)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
  }
  const recovery = immutableRecovery(identityKey, command, "CLAIMED", null);
  if (!saveStoredRecovery(recovery)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
  }
  pendingRecovery = recovery;
  pendingOutcomeMayExist = true;
  return { status: "OWNED", recovery };
}

function verifyRecoveryForDispatch(
  recovery: OfficialPaymentRecovery,
  identityKey: string,
): boolean {
  hydrateStoredRecovery();
  if (
    recoveryUnavailable ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey ||
    !verifyStoredRecovery(recovery)
  ) {
    recoveryUnavailable = true;
    return false;
  }
  return true;
}

export function officialPaymentRecoveryCanRetry(recovery: OfficialPaymentRecovery): boolean {
  return (
    recovery.state !== "DIRECTED" ||
    recovery.result?.directive.action === "WAIT_OR_QUERY_SAME_ATTEMPT"
  );
}

export function beginOfficialPaymentDispatch(
  recovery: OfficialPaymentRecovery,
  identityKey: string,
): boolean {
  if (
    dispatchingRecovery ||
    !officialPaymentRecoveryCanRetry(recovery) ||
    !verifyRecoveryForDispatch(recovery, identityKey)
  ) {
    return false;
  }
  dispatchingRecovery = recovery;
  return true;
}

export function finishOfficialPaymentDispatch(
  recovery: OfficialPaymentRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery !== recovery || recovery.ownerIdentityKey !== identityKey) return false;
  dispatchingRecovery = null;
  return true;
}

export function markOfficialPaymentOutcomeUnknown(
  recovery: OfficialPaymentRecovery,
  identityKey: string,
): boolean {
  if (!verifyRecoveryForDispatch(recovery, identityKey)) return false;
  if (recovery.state === "DIRECTED") return true;
  const unknown =
    recovery.state === "OUTCOME_UNKNOWN"
      ? recovery
      : immutableRecovery(identityKey, recovery.command, "OUTCOME_UNKNOWN", null);
  if (!saveStoredRecovery(unknown)) {
    recoveryUnavailable = true;
    return false;
  }
  pendingRecovery = unknown;
  pendingOutcomeMayExist = true;
  return true;
}

function clearOfficialPaymentRecovery(
  recovery: OfficialPaymentRecovery,
  identityKey: string,
): boolean {
  if (
    recoveryUnavailable ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey
  ) {
    return false;
  }
  const access = sessionStorageAccess();
  if (access.browser) {
    if (!access.storage) {
      recoveryUnavailable = true;
      return false;
    }
    try {
      const stored = readStoredRecovery(access.storage);
      if (!stored || !recoveryEquals(stored, recovery)) {
        recoveryUnavailable = true;
        return false;
      }
      access.storage.removeItem(OFFICIAL_PAYMENT_RECOVERY_STORAGE_KEY);
      if (access.storage.getItem(OFFICIAL_PAYMENT_RECOVERY_STORAGE_KEY) !== null) {
        recoveryUnavailable = true;
        return false;
      }
    } catch {
      recoveryUnavailable = true;
      return false;
    }
  }
  pendingRecovery = null;
  pendingOutcomeMayExist = false;
  return true;
}

export function recordOfficialPaymentResult(
  recovery: OfficialPaymentRecovery,
  identityKey: string,
  result: OfficialPaymentOperatorResultV1,
): boolean {
  if (
    !verifyRecoveryForDispatch(recovery, identityKey) ||
    !isOfficialPaymentOperatorResultV1(result) ||
    !resultMatchesCommand(result, recovery.command)
  ) {
    return false;
  }
  if (
    result.directive.canCreateNewAttempt ||
    (result.gatewaySettlement === "SETTLED" && result.directive.canRecognizeRevenue)
  ) {
    return clearOfficialPaymentRecovery(recovery, identityKey);
  }
  const directed = immutableRecovery(identityKey, recovery.command, "DIRECTED", result);
  if (!saveStoredRecovery(directed)) {
    recoveryUnavailable = true;
    return false;
  }
  pendingRecovery = directed;
  pendingOutcomeMayExist = true;
  return true;
}

export function officialPaymentOutcomeIsUnknown(
  error: unknown,
  recovery: OfficialPaymentRecovery,
): boolean {
  if (recovery.state !== "CLAIMED") return true;
  return !(
    isRecord(error) &&
    typeof error.code === "string" &&
    definiteNoProviderEffectCodes.has(error.code)
  );
}

export function clearOfficialPaymentAfterDefiniteNoEffect(
  recovery: OfficialPaymentRecovery,
  identityKey: string,
): boolean {
  return clearOfficialPaymentRecovery(recovery, identityKey);
}

export function resetOfficialPaymentRecoveryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
  const access = sessionStorageAccess();
  try {
    access.storage?.removeItem(OFFICIAL_PAYMENT_RECOVERY_STORAGE_KEY);
  } catch {
    // Test cleanup also resets the in-memory fail-closed state.
  }
}

export function resetOfficialPaymentRecoveryMemoryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
}
